import { useCallback, useState } from 'react';
import {
  buildExclusiveTriggerHandlers,
  type ExclusiveTriggerHandlerProps,
  type TooltipTriggerMode,
} from './tooltip-trigger.utils';

export type UseTooltipTriggerOptions = {
  mode: TooltipTriggerMode;
  open?: boolean;
  defaultOpen?: boolean;
  onOpenChange?: (open: boolean) => void;
};

export type UseTooltipTriggerResult = {
  /** Radix default hover/focus; no exclusive handler merge. */
  usesRadixHover: boolean;
  open: boolean | undefined;
  onOpenChange: ((open: boolean) => void) | undefined;
  exclusiveHandlers: ExclusiveTriggerHandlerProps | null;
  contentDismissProps: {
    onEscapeKeyDown?: () => void;
    onPointerDownOutside?: () => void;
  };
};

export function useTooltipTrigger({
  mode,
  open: openProp,
  defaultOpen = false,
  onOpenChange: onOpenChangeProp,
}: UseTooltipTriggerOptions): UseTooltipTriggerResult {
  const usesRadixHover = mode === 'hover';
  const [uncontrolledOpen, setUncontrolledOpen] = useState(defaultOpen);
  const isControlled = openProp !== undefined;

  const setOpen = useCallback(
    (next: boolean | ((prev: boolean) => boolean)) => {
      const resolved =
        typeof next === 'function'
          ? next(isControlled ? (openProp as boolean) : uncontrolledOpen)
          : next;
      if (!isControlled) {
        setUncontrolledOpen(resolved);
      }
      onOpenChangeProp?.(resolved);
    },
    [isControlled, onOpenChangeProp, openProp, uncontrolledOpen],
  );

  const handleExclusiveOpenChange = useCallback(
    (next: boolean) => {
      if (!next) {
        setOpen(false);
      }
    },
    [setOpen],
  );

  if (usesRadixHover) {
    return {
      usesRadixHover: true,
      open: openProp,
      onOpenChange: onOpenChangeProp,
      exclusiveHandlers: null,
      contentDismissProps: {},
    };
  }

  const open = isControlled ? openProp : uncontrolledOpen;

  return {
    usesRadixHover: false,
    open,
    onOpenChange: handleExclusiveOpenChange,
    exclusiveHandlers: buildExclusiveTriggerHandlers(mode, setOpen),
    contentDismissProps:
      mode === 'click'
        ? {
            onEscapeKeyDown: () => setOpen(false),
            onPointerDownOutside: () => setOpen(false),
          }
        : {},
  };
}
